import { useLazyQuery } from '@apollo/client';
import { Icon } from '@phosphor-icons/react';
import { gql } from '__generated__';
import {
  FundStatusEnumType,
  SyndicateStatusEnumType,
  SyndicateTypeEnumType,
} from '__generated__/graphql';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';

import ErrorMessage from 'components/ErrorMessage';

import LoadingIndicator from 'primitives/LoadingIndicator';

import alIndiaLogo from 'media/alIndiaLogo.png';

import ProfileSwitchBar from './ProfileSwitchBar';
import {
  getFundDashboardTabs,
  getInvestorTabs,
  getRuvDashboardTabs,
  getSyndicateDashboardTabs,
} from './sidebar-tabs';

const SYNDICATE_STATUS_QUERY = gql(`
  query SyndicateStatus($id: ID!) {
    syndicate(id: $id) {
      id
      status
      type
    }
  }
`);

const FUND_STATUS_QUERY = gql(`
  query FundStatus($id: ID!) {
    fund(id: $id) {
      id
      status
      syndicate {
        id
      }
    }
  }
`);

type SidebarTab = {
  key: string;
  label: string;
  icon: Icon;
  route: string;
  exact?: boolean;
  disabled?: boolean;
  trailingIcon?: React.ReactNode;
};

const SIDEBAR_FOOTER_TAB_KEYS = new Set(['settings', 'help']);

function partitionSidebarNavTabs(tabs: SidebarTab[]): { main: SidebarTab[]; footer: SidebarTab[] } {
  const footer = tabs.filter(t => SIDEBAR_FOOTER_TAB_KEYS.has(t.key));
  const main = tabs.filter(t => !SIDEBAR_FOOTER_TAB_KEYS.has(t.key));
  return { main, footer };
}

const Sidebar = ({ mobile = false }: { mobile?: boolean }) => {
  const [navSections, setNavSections] = useState<{ main: SidebarTab[]; footer: SidebarTab[] }>({
    main: [],
    footer: [],
  });
  const { syndicateId, fundId } = useParams<{ syndicateId: string; fundId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const [getSyndicateStatus, { loading: syndicateLoading, error: syndicateError }] =
    useLazyQuery(SYNDICATE_STATUS_QUERY);

  const [getFundStatus, { loading: fundLoading, error: fundError }] =
    useLazyQuery(FUND_STATUS_QUERY);

  useEffect(() => {
    const lowercaseURL = location.pathname.toLowerCase();

    const containsFund = lowercaseURL.includes('fund-dashboard');
    const containsSyndicate = lowercaseURL.includes('syndicate-dashboard');

    if (!containsFund && !containsSyndicate) {
      setNavSections(partitionSidebarNavTabs(getInvestorTabs()));
      return;
    }
    if (containsSyndicate && syndicateId) {
      (async () => {
        const syndicate = await getSyndicateStatus({ variables: { id: syndicateId } });
        if (
          syndicate.data?.syndicate.status === SyndicateStatusEnumType.Inactive ||
          syndicate.data?.syndicate.status === SyndicateStatusEnumType.Deleted
        ) {
          navigate(`/syndicate-dashboard/${syndicateId}/suspended`);
          return;
        }
        if (syndicate.data?.syndicate.type === SyndicateTypeEnumType.Ruv) {
          setNavSections(
            partitionSidebarNavTabs(
              getRuvDashboardTabs(syndicateId, syndicate.data?.syndicate.status)
            )
          );
        } else {
          setNavSections(
            partitionSidebarNavTabs(
              getSyndicateDashboardTabs(
                syndicateId,
                syndicate.data?.syndicate.status,
                syndicate.data?.syndicate.type
              )
            )
          );
        }
      })();
      return;
    }
    if (containsFund && fundId) {
      (async () => {
        const fund = await getFundStatus({ variables: { id: fundId } });
        if (fund.data?.fund.status === FundStatusEnumType.Inactive) {
          navigate(`/fund-dashboard/${fundId}/suspended`);
          return;
        }
        setNavSections(
          partitionSidebarNavTabs(
            getFundDashboardTabs(fundId, fund.data?.fund.status, !!fund.data?.fund.syndicate?.id)
          )
        );
      })();
      return;
    }
  }, [syndicateId, fundId, getSyndicateStatus, getFundStatus, location.pathname, navigate]);

  function renderContent() {
    if (syndicateLoading || fundLoading) return <LoadingIndicator />;

    if (syndicateError || fundError) {
      return <ErrorMessage error={syndicateError || fundError} />;
    }

    const allTabs = [...navSections.main, ...navSections.footer];

    function isTabActive(tab: SidebarTab) {
      if (tab?.exact) {
        return location.pathname === tab.route;
      }
      if (tab.route === '/') return false;

      // If an exact-match tab is active, prefix tabs are not
      const exactTabMatch = allTabs.find(t => t.exact && location.pathname === t.route);
      if (exactTabMatch) return false;

      const matches =
        location.pathname === tab.route || location.pathname.startsWith(tab.route + '/');
      if (!matches) return false;

      // Only the most specific (longest) matching prefix tab is active
      return !allTabs.some(
        other =>
          other.key !== tab.key &&
          !other.exact &&
          other.route !== '/' &&
          other.route.length > tab.route.length &&
          (location.pathname === other.route || location.pathname.startsWith(other.route + '/'))
      );
    }

    function renderTabLink(tab: SidebarTab) {
      const active = isTabActive(tab);
      const TabIcon = tab.icon;
      return (
        <li key={tab.key}>
          <NavLink
            to={tab.route}
            className={classNames(
              active
                ? 'bg-gray-200 text-indigo-600'
                : 'text-gray-700 hover:text-indigo-600 hover:bg-gray-100',
              tab.disabled ? 'cursor-default opacity-50 pointer-events-none' : null,
              'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-medium relative'
            )}
          >
            <TabIcon
              size={20}
              weight={active ? 'fill' : 'regular'}
              color="currentColor"
              className={classNames(
                active ? 'text-indigo-600' : 'text-gray-800 group-hover:text-indigo-600',
                'shrink-0 self-center'
              )}
              aria-hidden
            />
            {tab.label}
            {tab.trailingIcon ? (
              <>
                <div className="grow" />
                <div className="justify-self-end justify-items-end self-end absolute right-2 top-3">
                  {tab.trailingIcon}
                </div>
              </>
            ) : null}
          </NavLink>
        </li>
      );
    }

    return (
      <>
        <ProfileSwitchBar />
        <nav className="mt-1 flex min-h-0 flex-1 flex-col">
          <ul className="flex flex-col gap-y-1">{navSections.main.map(renderTabLink)}</ul>
          {navSections.footer.length > 0 ? (
            <ul className="mt-auto flex flex-col gap-y-1 border-t border-gray-200 pt-3">
              {navSections.footer.map(renderTabLink)}
            </ul>
          ) : null}
        </nav>
      </>
    );
  }

  return (
    <div
      className={classNames(
        'flex grow flex-col gap-y-3 overflow-y-auto bg-gray-50 px-3.5 pb-4',
        mobile ? 'border-none' : null
      )}
    >
      <NavLink to="/">
        <div className="flex h-16 shrink-0 items-center ml-1.5">
          <img className="h-6 w-auto" src={alIndiaLogo} alt="Your Company" />
        </div>
      </NavLink>
      {renderContent()}
    </div>
  );
};

export default Sidebar;
